import { useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { StatusBar } from '@capacitor/status-bar';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { selectUser } from '../../features/user/userSlice';
import { setupPushNotifications } from '../../utils/native/push-notifications';
import { IS_IOS, IS_NATIVE } from '../constants';

export function usePushNotificationSetup() {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isPushNotificationSetupComplete = useRef(false);

  useEffect(() => {
    /* istanbul ignore next -- @preserve */
    async function setupPNs() {
      await setupPushNotifications(navigate, user.locale);
      isPushNotificationSetupComplete.current = true;
    }

    /* istanbul ignore if -- @preserve */
    if (IS_NATIVE) {
      if (IS_IOS) StatusBar.show();
      // The user needs to be logged in before setting up PNs.
      if (user.isLoggedIn) {
        if (!isPushNotificationSetupComplete.current) setupPNs();
      }
    }
  }, [navigate, user]);

  // Re-register the FCM/APNS token whenever the app returns to the foreground.
  // Firebase can silently refresh the device token while the app is backgrounded,
  // which would leave the backend holding a stale token. Re-running setup on resume
  // re-fires the `registration` event with the current token and keeps the backend in sync.
  useEffect(() => {
    /* istanbul ignore next -- @preserve */
    if (!IS_NATIVE) return;

    /* istanbul ignore next -- @preserve */
    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && isPushNotificationSetupComplete.current && user.isLoggedIn) {
        void setupPushNotifications(navigate).catch((error) => {
          console.error('Push notification setup (app resume) failed:', error);
        });
      }
    });

    /* istanbul ignore next -- @preserve */
    return () => {
      listener.then((l) => l.remove());
    };
  }, [navigate, user.isLoggedIn]);
}
